// Which step-4 paywall a visitor gets, and telling the backend about it.
//
// Two arms:
//   a — the Figma Golden Path: review first, then the trial offering.
//   b — the ported React-Membership paywall: paywall first, then review.
//
// Assignment is sticky in localStorage rather than the flow's usual per-tab
// sessionStorage, because the arm is a fact about the PERSON: someone who opens
// the flow in a second tab, or comes back tomorrow from a win-back email, has to
// see the same paywall or the experiment measures nothing. Keys and TTL follow
// the convention already in this repo (`Registration.tsx`'s ab-test-home, and
// React-Membership's ab-test-promo).

import { Step4Variant } from "../../../shared/api/submission";

export const STEP4_EXPERIMENT = "submissionStep4Paywall";
// ?step4=a|b|c|d picks an arm on purpose, in every build (docs/30). SubmissionRoute
// keeps it on every flow URL from the first page on; any other value
// (?step4=auto) drops the pick and the visitor goes back to the four-way split.
export const STEP4_PARAM = "step4";

const STORAGE_KEY = "ab-test-submission-step4";
const STORAGE_DATE_KEY = "ab-test-submission-step4-date";
// Per-tab, so a pick survives a hop that drops the query string (another
// domain, a link built without it); SubmissionRoute puts it back on the URL.
const OVERRIDE_KEY = "sfStep4Variant";
// ?step4=auto seen in this tab: the next exposure asks the server to drop a
// pick stored on the account.
const RESET_KEY = "sfStep4Reset";
const EXPOSURE_KEY = "sfStep4Exposure";

const TTL_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export const STEP4_ARMS: Step4Variant[] = ["a", "b", "c", "d"];

export const isStep4Variant = (value: any): value is Step4Variant =>
  STEP4_ARMS.indexOf(value) >= 0;

// a and c pay last: the review, then the plan, and the payment sends it.
export const paysLast = (arm: Step4Variant | null | undefined): boolean => arm === "a" || arm === "c";
// b and d pay first: the plan, then the review and Send.
export const paysFirst = (arm: Step4Variant | null | undefined): boolean => arm === "b" || arm === "d";
// c and d go straight to Stripe, without the membership paywall page.
export const skipsPaywall = (arm: Step4Variant | null | undefined): boolean => arm === "c" || arm === "d";

const hasWindow = () => typeof window !== "undefined";

const readLocal = (key: string): string | null => {
  try {
    return hasWindow() ? window.localStorage.getItem(key) : null;
  } catch (e) {
    // Private mode, or storage disabled. A visitor with no storage gets a fresh
    // roll each time — worse for the experiment, but never a broken page.
    return null;
  }
};

const writeLocal = (key: string, value: string) => {
  try {
    if (hasWindow()) window.localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

// ?step4=a|b. Honoured in every build, so an arm can be picked on purpose
// (QA, a demo, a link sent to someone). It cannot poison the results: the
// exposure says the arm was forced, and a forced arm is left out of the count.
const readOverride = (search?: string): Step4Variant | null => {
  if (!hasWindow()) return null;
  try {
    const raw = new URLSearchParams(
      search !== undefined ? search : window.location.search
    ).get(STEP4_PARAM);
    if (raw !== null) {
      const value = raw.trim().toLowerCase();
      if (isStep4Variant(value)) {
        window.sessionStorage.setItem(OVERRIDE_KEY, value);
        return value;
      }
      // Any other value drops the pick, so ?step4=auto is the way back to the
      // real assignment, never a third arm.
      window.sessionStorage.removeItem(OVERRIDE_KEY);
      window.sessionStorage.setItem(RESET_KEY, "1");
      return null;
    }
    const stored = window.sessionStorage.getItem(OVERRIDE_KEY);
    return isStep4Variant(stored) ? stored : null;
  } catch (e) {
    return null;
  }
};

const storedAssignment = (nowMs: number): Step4Variant | null => {
  const value = readLocal(STORAGE_KEY);
  if (!isStep4Variant(value)) return null;
  const assignedAt = Date.parse(readLocal(STORAGE_DATE_KEY) || "");
  // A missing or unparseable date is treated as expired: better to re-roll once
  // than to keep an assignment we cannot date.
  if (isNaN(assignedAt) || nowMs - assignedAt > TTL_DAYS * DAY_MS) return null;
  return value;
};

// What arm is this visitor in? Assigns and persists on first call, so calling it
// from a render is safe — the coin is flipped once, not on every paint.
export const resolveStep4Variant = (
  search?: string,
  nowMs: number = Date.now()
): Step4Variant => {
  const forced = readOverride(search);
  if (forced) return forced;

  const existing = storedAssignment(nowMs);
  if (existing) return existing;

  // A quarter each (docs/30 §10).
  const rolled: Step4Variant = STEP4_ARMS[Math.min(STEP4_ARMS.length - 1, Math.floor(Math.random() * STEP4_ARMS.length))];
  writeLocal(STORAGE_KEY, rolled);
  writeLocal(STORAGE_DATE_KEY, new Date(nowMs).toISOString());
  return rolled;
};

// Was the arm picked by URL rather than assigned? Sent with the exposure, so a
// forced arm is never counted in the results.
export const isStep4Forced = (search?: string): boolean => readOverride(search) !== null;

// The search string with the tab's pick added back, or null when it needs no
// change: no pick, or the URL already says. Raw concatenation rather than a
// re-serialisation, so every other parameter reaches the page exactly as sent.
export const carryStep4 = (search: string): string | null => {
  const forced = readOverride(search);
  if (!forced) return null;
  if (new URLSearchParams(search).has(STEP4_PARAM)) return null;
  const rest = search.replace(/^\?/, "");
  return `?${rest}${rest ? "&" : ""}${STEP4_PARAM}=${forced}`;
};

// Read without assigning — for code that wants to label an event but must not
// enrol someone who never reached step 4.
export const peekStep4Variant = (nowMs: number = Date.now()): Step4Variant | null =>
  readOverride() || storedAssignment(nowMs);

// The backend is told once per session per arm, so a remount, a back-navigation
// or a second tab does not inflate the experiment's denominator. The mock
// enforces the same rule server-side; this just avoids the pointless call.
export const shouldRecordExposure = (variant: Step4Variant): boolean => {
  try {
    if (!hasWindow()) return false;
    if (window.sessionStorage.getItem(EXPOSURE_KEY) === variant) return false;
    window.sessionStorage.setItem(EXPOSURE_KEY, variant);
    return true;
  } catch (e) {
    // No storage: send it. A duplicate exposure is better than a missing one,
    // and the server deduplicates anyway.
    return true;
  }
};

// Read once by the exposure: was a pick dropped in this tab since the last one?
export const consumeStep4Reset = (): boolean => {
  try {
    if (!hasWindow()) return false;
    const reset = window.sessionStorage.getItem(RESET_KEY) === "1";
    window.sessionStorage.removeItem(RESET_KEY);
    return reset;
  } catch (e) {
    return false;
  }
};

// The server keeps the account's arm (docs/30 #19): another device, a win-back
// link or an earlier pick may have stored a different one, and that one wins.
export const adoptStep4Variant = (arm: Step4Variant, nowMs: number = Date.now()) => {
  if (!isStep4Variant(arm)) return;
  if (readOverride() && readOverride() !== arm) {
    try {
      window.sessionStorage.setItem(OVERRIDE_KEY, arm);
    } catch (e) {
      // ignore
    }
  }
  writeLocal(STORAGE_KEY, arm);
  writeLocal(STORAGE_DATE_KEY, new Date(nowMs).toISOString());
};

// Test and demo helper: forget the assignment so the next resolve re-rolls.
export const clearStep4Variant = () => {
  try {
    if (!hasWindow()) return;
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_DATE_KEY);
    window.sessionStorage.removeItem(OVERRIDE_KEY);
    window.sessionStorage.removeItem(EXPOSURE_KEY);
    window.sessionStorage.removeItem(RESET_KEY);
  } catch (e) {
    // ignore
  }
};
